import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Link2, Copy } from "lucide-react";
import { AdPreviews } from "@/components/AdPreviews";
import { SignupSources } from "@/components/SignupSources";

const area = "w-full h-28 bg-black/30 border border-white/10 rounded-xl p-3 font-mono text-xs focus:border-gold outline-none";

const CodeField = ({ id, label, hint, value, onChange }) => (
  <label className="block space-y-2">
    <span className="text-[10px] uppercase tracking-[0.2em] text-gold">{label}</span>
    <textarea data-testid={`marketing-${id}`} value={value} onChange={(e) => onChange(e.target.value)} className={area} placeholder="Paste the code from your ad network" spellCheck={false} />
    <span className="block text-[11px] text-text-muted">{hint}</span>
  </label>
);

const LinkBuilder = () => {
  const [f, setF] = useState({ source: "", campaign: "" });
  const url = `${window.location.origin}/?utm_source=${encodeURIComponent(f.source || "network")}&utm_medium=ads${f.campaign ? `&utm_campaign=${encodeURIComponent(f.campaign)}` : ""}`;
  return (
    <div data-testid="marketing-link-builder" className="rounded-2xl border border-white/8 bg-surface p-5 space-y-3">
      <p className="text-sm flex items-center gap-2"><Link2 size={14} className="text-gold" /> Campaign link</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="link-source" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="Ad network, e.g. exoclick" className="bg-transparent border-b border-white/15 py-2 text-sm outline-none focus:border-gold" />
        <input data-testid="link-campaign" value={f.campaign} onChange={(e) => setF({ ...f, campaign: e.target.value })} placeholder="Campaign, e.g. launch-nov" className="bg-transparent border-b border-white/15 py-2 text-sm outline-none focus:border-gold" />
      </div>
      <div className="flex items-center gap-2">
        <code data-testid="link-output" className="flex-1 text-[11px] text-text-muted break-all">{url}</code>
        <button data-testid="link-copy" onClick={() => (navigator.clipboard ? navigator.clipboard.writeText(url) : Promise.reject()).then(() => toast.success("Link copied")).catch(() => toast.error("Copy blocked by the browser. Select the link and copy it manually."))} className="p-2 rounded-full border border-white/10 hover:border-gold"><Copy size={13} /></button>
      </div>
      <p className="text-[11px] text-text-muted">Use this as the destination URL in your ad. Sign-ups from it are counted below.</p>
    </div>
  );
};

export function MarketingAdmin() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/marketing").then(({ data }) => setS(data)); }, []);
  if (!s) return <p className="text-text-muted">Loading…</p>;
  const set = (k) => (v) => setS({ ...s, [k]: v });
  const save = async () => {
    setBusy(true);
    try { setS((await api.put("/admin/marketing", s)).data); toast.success("Ad settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || "Couldn't save"); }
    setBusy(false);
  };
  return (
    <div data-testid="marketing-admin" className="space-y-10">
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <CodeField id="head-code" label="Tracking code (every page)" hint="Your network's base pixel or tag. Loads once on every page." value={s.head_code} onChange={set("head_code")} />
          <CodeField id="signup-code" label="Sign-up conversion code" hint="Fires once, right after someone creates an account." value={s.signup_code} onChange={set("signup_code")} />
          <CodeField id="banner-code" label="Banner ad code" hint="Shown above Discover when switched on. Runs in a sealed frame." value={s.banner_code} onChange={set("banner_code")} />
          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <input data-testid="marketing-banner-enabled" type="checkbox" checked={s.banner_enabled} onChange={(e) => set("banner_enabled")(e.target.checked)} className="w-4 h-4 accent-[#D4AF37]" />
            Show the banner to members on Discover
          </label>
          <button data-testid="marketing-save" disabled={busy} onClick={save} className="flex items-center gap-2 px-7 py-3 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.2em] disabled:opacity-50">
            <Save size={14} /> {busy ? "Saving…" : "Save ad settings"}
          </button>
        </div>
        <AdPreviews code={s.banner_code} />
      </div>
      <LinkBuilder />
      <SignupSources />
    </div>
  );
}
